// src/components/WalletModal/WalletModal.js
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import all wallet images from src/walls directory
const wallContext = require.context('../../walls', false, /\.webp$/);

// Helper to format wallet names properly
const formatWalletName = (fileName) => {
  // Handle special cases and acronyms
  const specialCases = {
    'mew': 'MEW',
    'okx': 'OKX',
    'cvl walle': 'CVL Wallet',
    'bcertain wallet': 'BCertain Wallet',
    'saakuru ': 'Saakuru',
    'wemix': 'WEMIX',
    'wemix wallet': 'WEMIX Wallet',
    'walletconnect': 'WalletConnect',
    'trust': 'Trust Wallet',
    'metamask': 'MetaMask',
    'binance': 'Binance',
    'coinbase': 'Coinbase',
    'exodus': 'Exodus',
    'ledger': 'Ledger',
    'trezor': 'Trezor',
    'd\'cent': 'D\'CENT',
    'frontier ': 'Frontier',
    'unstoppable wa': 'Unstoppable Wallet',
    'wombat ': 'Wombat',
    '1inch': '1inch',
    'okto': 'OKTO',
    'okse': 'OKSE',
  };
  
  const lower = fileName.toLowerCase().trim();
  if (specialCases[lower]) {
    return specialCases[lower];
  }
  
  // Capitalize first letter of each word, handle special characters
  return fileName
    .split(/[\s\-_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper to create URL-safe ID from filename
const createWalletId = (fileName) => {
  return fileName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace spaces/special chars with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Load all wallets dynamically
const wallets = wallContext.keys()
  .map(path => {
    const fileName = path.split('/').pop().replace(/\.webp$/, '');
    const walletData = {
      name: formatWalletName(fileName),
      icon: wallContext(path),
      id: createWalletId(fileName)
    };
    return walletData;
  })
  .sort((a, b) => {
    // Custom sort: prioritize popular wallets first
    const priority = {
      'walletconnect': 1,
      'trust': 2,
      'trust-wallet': 2,
      'metamask': 3,
      'coinbase': 4,
      'binance': 5,
      'exodus': 6,
      'ledger': 7,
      'phantom': 8,
      'okx': 9,
      'okto': 10,
    };
    const priorityA = priority[a.id] || 999;
    const priorityB = priority[b.id] || 999;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.name.localeCompare(b.name);
  });

const WalletModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  
  const handleWalletClick = (wallet) => {
    onClose();
    navigate(`/connect/${wallet.id}`, { 
      state: { 
        wallet: {
          name: wallet.name,
          icon: wallet.icon
        }
      }
    });
  };

  // Modal animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { delay: 0.1 }
    },
    exit: { 
      scale: 0.95, 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            variants={modalVariants}
            className="bg-[#1a1209] rounded-2xl w-full max-w-md relative"
          >
            {/* Header with fixed position */}
            <div className="sticky top-0 bg-[#1a1209] p-6 pb-4 rounded-t-2xl border-b border-[#4a2f17]/40 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">Connect to a wallet</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable wallet list */}
            <div className="max-h-[60vh] overflow-y-auto p-6 pt-4">
              <div className="space-y-3">
                {wallets.map((wallet, index) => (
                  <motion.button
                    key={wallet.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleWalletClick(wallet)}
                    className="w-full flex items-center justify-between p-4 rounded-xl 
                             bg-[#24180e] hover:bg-[#2d2013] transition-all duration-200
                             text-white border border-[#4a2f17]/40 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-[#a16207]"></div>
                      <span className="font-medium">{wallet.name}</span>
                    </div>
                    <img 
                      src={wallet.icon} 
                      alt={`${wallet.name} logo`}
                      className="h-8 w-8 object-contain transform transition-transform duration-200 group-hover:scale-110"
                      loading="lazy"
                    />
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WalletModal;